import { EventTracking } from "@shared/services/event_tracking/events";
import { CartApiMetadataEntity, CartProductEntity } from "@shared/services/event_tracking/entities";
import { HttpPromise } from "@shared/svelte/Modules/ApiService";
import * as _ from "lodash";
const { trackEvent } = EventTracking;

const service = function ($rootScope, Tracking, CartApi, CartApiCallbacks, RProductLine, Flash) {
  $rootScope.cart = {};

  const trackCartEvent = function (
    event_name,
    product_id,
    product_line_id,
    distributor_id,
    order_group_id,
    order_id,
    inventory_id,
    unit_quantity,
    case_quantity,
    stockLevel,
    extraTrackingEntities,
  ) {
    const entities = [
      new CartApiMetadataEntity({
        type: "distributor",
        id: distributor_id,
      }),
      new CartApiMetadataEntity({
        type: "order",
        id: order_id,
      }),
      new CartApiMetadataEntity({
        type: "order_group",
        id: order_group_id,
      }),
      new CartProductEntity({
        productId: product_id,
        productLineId: product_line_id,
        unitQuantity: unit_quantity,
        caseQuantity: case_quantity,
        distributor_id: distributor_id,
        order_group_id: order_group_id,
        order_id: order_id,
        inventory_id: inventory_id,
        stockLevel: stockLevel,
      }),
    ].concat(extraTrackingEntities || []);
    trackEvent(event_name, entities);
  };

  const removeProduct = function (product, callback, skipPlaceholder, extraTrackingEntities) {
    $rootScope.showLoading = true;
    const originalCartProduct = CartApiCallbacks.cartProductByInventory(
      product.inventoryId || product.inventory_id,
    );
    if (!originalCartProduct) {
      if (callback) {
        callback({ error: "No cart product from inventory id!" });
      }
      $rootScope.showLoading = false;
      return;
    }
    originalCartProduct.unit_diff = originalCartProduct.unit_quantity * -1;
    originalCartProduct.case_diff = originalCartProduct.case_quantity * -1;
    const promise = CartApi.remove(
      _.merge(originalCartProduct, product),
      callback,
      skipPlaceholder,
    );

    promise.then(function (res) {
      trackCartEvent(
        "cart_remove",
        originalCartProduct.product_id,
        originalCartProduct.product_line.id,
        originalCartProduct.distributor_id,
        $rootScope?.cart?.id,
        originalCartProduct.order_id,
        originalCartProduct.inventory_id,
        Math.abs(originalCartProduct.unit_diff),
        Math.abs(originalCartProduct.case_diff),
        product.stockLevel,
        extraTrackingEntities,
      );
    });

    return promise;
  };

  const updateProductQuantity = function (product, callback, extraTrackingEntities) {
    $rootScope.showLoading = true;
    let cartProduct = CartApiCallbacks.cartProductByInventory(product.inventory_id);
    if (!cartProduct) {
      product.unit_diff = product.unit_quantity;
      product.case_diff = product.case_quantity;
      product = _.merge(product, Tracking.filterParams("previous"));
      const promise = CartApi.add(product, callback);
      promise.then(function (res) {
        const cartProduct = $rootScope.inventoryHash[product.inventory_id] || {};
        if (!cartProduct || !cartProduct.product_line) {
          return;
        }
        trackCartEvent(
          "cart_add",
          cartProduct.product_id,
          cartProduct.product_line.id,
          cartProduct.distributor_id,
          $rootScope?.cart?.id,
          cartProduct.order_id,
          cartProduct.inventory_id,
          cartProduct.unit_quantity,
          cartProduct.case_quantity,
          product.stockLevel,
          extraTrackingEntities,
        );
        $rootScope.showLoading = false;
      });
    } else {
      product.id = cartProduct.id;
      product.unit_diff = product.unit_quantity - cartProduct.unit_quantity;
      product.case_diff = product.case_quantity - cartProduct.case_quantity;
      const promise = CartApi.updateQuantity(product, callback);
      promise.then(function () {
        // set up data in advance of reload
        // should move to a quicker system where we don't reload entire cart every change
        $rootScope.inventoryHash[product.inventory_id] = _.merge(
          $rootScope.inventoryHash[product.inventory_id],
          { unit_quantity: product.unit_quantity, case_quantity: product.case_quantity },
        );
        if (product.case_diff > 0 || product.unit_diff > 0) {
          trackCartEvent(
            "cart_add",
            _.get(cartProduct, "product_id"),
            _.get(cartProduct, "product_line.id"),
            cartProduct.distributor_id,
            $rootScope?.cart?.id,
            cartProduct.order_id,
            cartProduct.inventory_id,
            product.unit_diff,
            product.case_diff,
            product.stockLevel,
            extraTrackingEntities,
          );
        } else {
          trackCartEvent(
            "cart_remove",
            _.get(cartProduct, "product_id"),
            _.get(cartProduct, "product_line.id"),
            cartProduct.distributor_id,
            $rootScope?.cart?.id,
            cartProduct.order_id,
            cartProduct.inventory_id,
            Math.abs(product.unit_diff),
            Math.abs(product.case_diff),
            product.stockLevel,
            extraTrackingEntities,
          );
          $rootScope.showLoading = false;
        }
      });
      return promise;
    }
  };

  const onCartWidgetChange = function (
    inventoryId,
    unitQty,
    caseQty,
    additionalProductParams,
    trackingParams,
    extraTrackingEntities,
    completed,
  ) {
    if (!additionalProductParams) {
      additionalProductParams = {};
    }

    $rootScope.showLoading = true;
    const product = _.merge(
      {
        inventory_id: inventoryId,
        inventoryId: inventoryId,
        unit_quantity: unitQty,
        case_quantity: caseQty,
        ...additionalProductParams,
      },
      trackingParams,
    );

    const skipPlaceholder = trackingParams.location != "cart";
    if (unitQty == 0 && caseQty == 0) {
      removeProduct(
        product,
        function (err, res) {
          completed(err == null);
        },
        skipPlaceholder,
        extraTrackingEntities,
      );
      return;
    }

    updateProductQuantity(
      product,
      function (err, res) {
        completed(err == null);
      },
      extraTrackingEntities,
    );
  };

  const mergeMatchedCartProductToProduct = function (product) {
    if (!product) {
      return;
    }

    if (!product.inventory_id) {
      return product;
    }

    const cartProduct = CartApiCallbacks.cartProductByInventory(product.inventory_id);
    if (!cartProduct) {
      return product;
    }

    return {
      ...product,
      case_quantity: cartProduct.case_quantity,
      unit_quantity: cartProduct.unit_quantity,
      inventory: cartProduct.inventory,
      inventory_below_minimum: cartProduct.inventory_below_minimum,
      inventory_is_low_stock: cartProduct.inventory_is_low_stock,
      inventory_is_completely_out_of_stock: cartProduct.inventory_is_completely_out_of_stock,
      allocation: cartProduct.allocation,
      inventory_id: cartProduct.inventory_id,
      is_available: cartProduct.is_available,
      pickup_is_selected: !!cartProduct.warehouse_id,
      warehouse_id: cartProduct.warehouse_id,
    };
  };

  const canSubmit = function () {
    return !(
      CartApiCallbacks.hasAnyProductsNotAvailable() ||
      CartApiCallbacks.hasAnyDistributorsNotConnected() ||
      CartApiCallbacks.hasProductsOutOfStock() ||
      CartApiCallbacks.liquorLicenseMissing() ||
      CartApiCallbacks.doesNotMeetTabcSpiritDeliveryMin() ||
      CartApiCallbacks.doesNotMeetMinimumOrderQuantity() ||
      CartApiCallbacks.integratedDistributorsBlockedOnCustomOrderBlockingFlag().length > 0 ||
      CartApiCallbacks.hasUnconfirmedAutoMatchedDistributor() ||
      CartApiCallbacks.isBelowMandatoryMinimum() ||
      CartApiCallbacks.requiresRetailerUserAuthentication() ||
      CartApiCallbacks.hasOnlyRequestedItems() ||
      CartApiCallbacks.hasNonOrderableProducts() ||
      CartApiCallbacks.hasNonTxPartnerOrders() ||
      CartApiCallbacks.hasAnyOrdersWithConnectedAccountInactive()
    );
  };

  const canSubmitIndividualOrder = function (order) {
    return !(
      CartApiCallbacks.orderHasAnyProductsNotAvailable(order) ||
      CartApiCallbacks.orderDistributorNotConnected(order) ||
      CartApiCallbacks.liquorLicenseMissing() ||
      CartApiCallbacks.orderDoesNotMeetTabcSpiritDeliveryMin(order) ||
      CartApiCallbacks.orderDoesNotMeetMinimumOrderQuantity(order) ||
      CartApiCallbacks.orderBlockedOnCustomOrderBlockingFlag(order) ||
      CartApiCallbacks.orderHasUnconfirmedAutoMatchedDistributor(order) ||
      CartApiCallbacks.orderDistributorNotAcceptingOrders(order) ||
      CartApiCallbacks.orderIsBelowMandatoryMinimum(order) ||
      CartApiCallbacks.requiresRetailerUserAuthentication(order) ||
      CartApiCallbacks.orderHasOnlyRequestedItems(order) ||
      CartApiCallbacks.orderHasProductsNotSoldAtThisLocation(order) ||
      CartApiCallbacks.orderHasNonOrderableProducts(order) ||
      CartApiCallbacks.retailerHasNotPaidTXSubscription(order) ||
      (order.distributor.allow_ordering_out_of_stock === false &&
        CartApiCallbacks.orderHasProductsOutOfStock(order)) ||
      CartApiCallbacks.orderConnectedAccountInactive(order)
    );
  };

  const updateOrder = (params, orderId) => {
    return HttpPromise({
      url: "/api/retailer/orders/" + orderId,
      data: {
        order: {
          ...params,
        },
      },
      method: "PUT",
    }).catch(e => {
      e.forEach(message => {
        Flash.setMessage(message, true, "error", "Error");
      });
    });
  };

  const savePONumber = async (orderId, poNumber, callback) => {
    try {
      const response = await $http({
        method: "PUT",
        url: "/api/retailer/orders/" + orderId,
        data: {
          order: {
            po_number: poNumber,
          },
        },
      });
      if (callback) return callback(response);
    } catch (e) {
      e.forEach(message => {
        Flash.setMessage(message, true, "error", "Error");
      });
    }
  };

  const updateOrderPONumber = (order, callback) => {
    return savePONumber(order.id, order.po_number, callback);
  };

  return {
    add: CartApi.add,
    load: CartApi.load,
    loadCount: CartApi.loadCount,
    inventoryMatchString: CartApiCallbacks.inventoryMatchString,
    inventoryMatchStrings: CartApiCallbacks.inventoryMatchStrings,
    cartProductByInventory: CartApiCallbacks.cartProductByInventory,
    submitForApproval: CartApi.submitForApproval,
    submit: CartApi.submit,
    submitForPunchout: CartApi.submitForPunchout,
    submitIndividualOrder: CartApi.submitIndividualOrder,
    onCartWidgetChange: onCartWidgetChange,
    removeProduct: removeProduct,
    prepInventory: CartApiCallbacks.prepInventory,
    updateQuantity: CartApi.updateQuantity,
    updateDeliveryOrPickupLocation: CartApi.updateDeliveryOrPickupLocation,
    batchUpdateOrderWarehouses: CartApi.batchUpdateOrderWarehouses,
    batchUpdateOrders: CartApi.batchUpdateOrders,
    getFulfillmentInformation: CartApi.getFulfillmentInformation,
    saveNote: CartApi.saveNote,
    RProductLine: RProductLine,
    Flash: Flash,
    mergeMatchedCartProductToProduct: mergeMatchedCartProductToProduct,
    inventoryHash: function () {
      return $rootScope.inventoryHash;
    },
    canSubmit,
    canSubmitIndividualOrder,
    updateOrder,
    updateOrderPONumber,
    integratedDistributorsBlockedOnCustomOrderBlockingFlag:
      CartApiCallbacks.integratedDistributorsBlockedOnCustomOrderBlockingFlag,
    orderHasProductsOutOfStock: CartApiCallbacks.orderHasProductsOutOfStock,
    orderHasProductsNotSoldAtThisLocation: CartApiCallbacks.orderHasProductsNotSoldAtThisLocation,
    orderHasProductNotFoundProducts: CartApiCallbacks.orderHasProductNotFoundProducts,
    orderHasNonOrderableProducts: CartApiCallbacks.orderHasNonOrderableProducts,
    orderHasOnlyRequestedItems: CartApiCallbacks.orderHasOnlyRequestedItems,
    orderBlockedOnCustomOrderBlockingFlag: CartApiCallbacks.orderBlockedOnCustomOrderBlockingFlag,
  };
};

export default service;
